import { useState, useRef, useMemo, useEffect } from "react";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import StatCard from "./StatCard.js";
import TicketCard from "./TicketCard.js";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import GlobalDropdownField from "../../../shared/utils/GlobalDropdownField.jsx";
import ResolveInstallation from "../../LandingPages/ResolveInstallation.jsx";
import { Dialog } from "@mui/material";

export default function InstallationFlag() {
  const productDropdownRef = useRef(null);
  const serverURL = process.env.REACT_APP_URL;
  const [loading, setLoading] = useState(true);
  const [loadingTicketId, setLoadingTicketId] = useState(null);
  const [error, setError] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [productOptions, setProductOptions] = useState([]);
  const [productType, setProductType] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const [resolvedFlagModal, setResolvedFlagModal] = useState(false);
  const [flagData, setFlagData] = useState(null);

  const [stats, setStats] = useState({
    incorrectInstallation: 0,
    poorFinishing: 0,
    damagedElement: 0,
    missingElement: 0,
    resolveRate: 0,
  });
  const [tabCount, setTabCount] = useState({
    all: 0,
    flagged: 0,
    resolved: 0,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        productDropdownRef.current &&
        !productDropdownRef.current.contains(event.target)
      ) {
        setIsProductDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchTickets = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${serverURL}/installation-flags`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          page,
          limit,
          search: debouncedSearch,
          productType,
          status,
          sortOrder,
        },
      });

      setTickets(response.data?.data?.tickets);
      console.log(response.data?.data?.tickets);

      const statCardData = response.data?.data?.statistics?.statCardData;
      const tabsCountData = response.data?.data?.statistics;

      setStats({
        incorrectInstallation: statCardData?.incorrectInstallation ?? 0,
        poorFinishing: statCardData?.poorFinishing ?? 0,
        damagedElement: statCardData?.damagedElement ?? 0,
        missingElement: statCardData?.missingElement ?? 0,
        resolveRate: statCardData?.resolveRate ?? 0,
      });

      setTabCount({
        all: tabsCountData?.totalTickets ?? 0,
        flagged: tabsCountData?.flaggedTickets ?? 0,
        resolved: tabsCountData?.resolvedTickets ?? 0,
      });

      console.log(response.data);
    } catch (error) {
      console.error("Error fetching installation tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchItemMasterProductList = async () => {
    try {
      const res = await fetch(`${serverURL}/itemMaster/all-product-type`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const data = await res.json();
      setProductOptions((prev) => ["All Product", ...data?.products]);

      return data?.products || [];
    } catch (error) {
      console.error("Error fetching item master product list:", error);
      return [];
    }
  };

  useEffect(() => {
    fetchItemMasterProductList();
  }, []);

  const TABS = [
    { key: "", label: "All Tickets", count: tabCount?.all },
    { key: "Flagged", label: "Flagged", count: tabCount?.flagged },
    { key: "Resolved", label: "Resolved", count: tabCount?.resolved },
  ];

  const STATS = [
    {
      label: "Installation Issue",
      value: `${stats.incorrectInstallation}%`,
    },
    {
      label: "Finishing Issue",
      value: `${stats.poorFinishing}%`,
    },
    {
      label: "Damaged Issue",
      value: `${stats.damagedElement}%`,
    },
    {
      label: "Missing Element Issue",
      value: `${stats.missingElement}%`,
    },
    {
      label: "Resolve rate",
      value: `${stats.resolveRate}%`,
      highlight: true,
    },
  ];

  useEffect(() => {
    fetchTickets();
  }, [page, limit, debouncedSearch, productType, status, sortOrder]);

  const handleOpenResolvedFlag = async (ticket) => {
    try {
      setError("");
      setLoadingTicketId(ticket._id);

      const response = await axios.get(
        `${serverURL}/installation-flags/${ticket.productId._id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      const responseData = response?.data?.data;

      setFlagData(responseData);
      setResolvedFlagModal(true);
    } catch (error) {
      console.error("Get installation flag error:", error);

      setError(
        error?.response?.data?.message ||
          "Something went wrong while getting installation flag",
      );
    } finally {
      setLoadingTicketId(null);
    }
  };

  const handleCloseResolvedFlag = () => {
    setResolvedFlagModal(false);
    setError("");
  };

  const handleFlagResolve = (status) => {
    fetchTickets();
  };

  return (
    <div>
      <div style={{ padding: "1rem 2rem", borderBottom: "1px solid #ddddddd" }}>
        <h2 className="text-primary font-bold">Tickets</h2>
      </div>
      <div className="px-8">
        {/* Stats */}
        <div className="mb-[18px] flex flex-wrap gap-3 my-3">
          {STATS.map((s) => (
            <StatCard
              key={s.label}
              label={s.label}
              value={s.value}
              highlight={s.highlight}
            />
          ))}
        </div>

        {/* Tabs + Filters */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2.5">
          {/* Tabs */}
          <div className="flex gap-[22px]">
            {TABS.map((t) => (
              <h5
                key={t.key}
                onClick={() => setStatus(t.key)}
                className={`cursor-pointer border-0 bg-transparent px-0 pb-2 text-[13px] ${
                  status === t.key
                    ? "border-b-2 border-[#1A1D23] font-bold text-[#1A1D23]"
                    : "border-b-2 border-transparent font-medium text-[#8B9099]"
                }`}
              >
                {t.label} ({t.count})
              </h5>
            ))}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            {/* Search */}
            <div className="flex min-w-[220px] items-center gap-1.5 rounded-lg border border-[#E6E8EC] bg-white px-[16px] py-[8px]">
              <SearchIcon className="text-[16px] text-[#9AA3AF]" />

              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by ticket id, campaign, vendor"
                className="w-full border-none bg-transparent text-primary outline-none placeholder:text-secondary placeholder: text-[14px]"
              />
            </div>

            {/* Product Filter */}
            <div ref={productDropdownRef} className="relative">
              <button
                type="button"
                onClick={() => setIsProductDropdownOpen((prev) => !prev)}
                className="flex items-center gap-1.5 rounded-lg w-[150px] overflow-x-hidden border border-[#E6E8EC] bg-white px-[16px] py-[8px] text-[12.5px] font-medium text-[#3D4451]"
              >
                <h6 className=" flex-1 truncate text-left">
                  {productType || "All Product"}
                </h6>

                <KeyboardArrowDownIcon
                  className={`text-[15px] text-[#9AA3AF] transition-transform ${
                    isProductDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isProductDropdownOpen && (
                <div className="absolute right-0 top-full z-50 mt-1 max-h-[400px] min-w-[160px] overflow-y-auto rounded-lg border border-[#E6E8EC] bg-white py-1 shadow-md">
                  {productOptions.map((option) => {
                    const value = option === "All Product" ? "" : option;

                    return (
                      <button
                        key={option}
                        type="button"
                        onClick={() => {
                          setProductType(value);
                          setPage(1);
                          setIsProductDropdownOpen(false);
                        }}
                        className={`block w-full px-3 py-2 text-left text-[12.5px] hover:bg-[#F5F6F8] ${
                          productType === value
                            ? "font-medium text-[#3D4451]"
                            : "text-[#6B7280]"
                        }`}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* <GlobalDropdownField
                variant="compact"
                name="productTypeFilter"
                value={productType || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setProductType(value);
                  setPage(1);
                }}
                placeholder="All Product"
                options={[
                  { value: "", label: "All Product" },
                  ...productOptions
                    .filter((option) => option !== "All Product")
                    .map((option) => ({ value: option, label: option })),
                ]}
                renderValueFn={(selected) => {
                  if (!selected) {
                    return "Filter Product";
                  }
                  const selectedOption = [
                    { value: "", label: "All Product" },
                    ...productOptions
                      .filter((option) => option !== "All Product")
                      .map((option) => ({ value: option, label: option })),
                  ].find((option) => option.value === selected);
                  return <p> {selectedOption?.label || "Filter Product"} </p>;
                }}
                useNewStyles={true}
              /> */}
            </div>

            {/* Sort */}
            <h6
              onClick={() =>
                setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"))
              }
              className="flex items-center gap-1.5 rounded-lg border border-[#E6E8EC] bg-white px-[16px] py-[8px] font-medium text-primary cursor-pointer"
            >
              <SwapVertIcon
                className={` ${
                  sortOrder === "asc" ? "text-secondary" : "text-primary"
                }`}
              />
              Last updated
            </h6>
          </div>
        </div>

        {/* Tickets Grid */}
        <div className="grid min-h-[400px] max-h-[calc(100vh-300px)] grid-cols-1 gap-4 overflow-y-auto md:grid-cols-2 lg:grid-cols-3">
          {loading ? (
            <div className="col-span-full flex h-full items-center justify-center">
              <CircularProgress size={32} />
            </div>
          ) : tickets.length === 0 ? (
            <div className="col-span-full flex h-full items-center justify-center">
              <p className="text-sm font-medium text-[#6B7280]">
                No tickets found
              </p>
            </div>
          ) : (
            tickets.map((ticket) => (
              <TicketCard
                key={ticket._id}
                ticket={ticket}
                onAction={handleOpenResolvedFlag}
                loading={loadingTicketId === ticket._id}
              />
            ))
          )}

          <Dialog open={resolvedFlagModal} maxWidth="md" fullWidth>
            <ResolveInstallation
              data={flagData}
              onClose={handleCloseResolvedFlag}
              onFlagResolve={handleFlagResolve}
              setLoading={setLoading}
              loading={loading}
            />
          </Dialog>
        </div>
      </div>
    </div>
  );
}
